import React, { memo, useRef, useEffect } from "react";
import { useLocation } from "react-router";
import { observer } from "mobx-react";
import {
  Box,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import HomeIcon from "./icons/HomeIcon";
import { ThumbnailIcon } from "./icons/ThumbnailIcon";
import { ClaimsIcon } from "./icons";
import DashboardIcon from "./icons/DashboardIcon";
import HelpIcon from "./icons/HelpIcon";

import { useAppStore } from "../hooks/useAppStore";
import UserProfileMenu from "./UserProfileMenu";
import NavItemComponent, { NavItem } from "./ui/header/NavItemComponent";
import { useClaimsStore } from "../hooks/useClaimsStore";
import { useChatStore } from "../hooks/useChatStore";
import { useChatCategoryStore } from "../hooks/useChatCategoryStore";
import { useChannels } from "../contexts/ChannelsContext";
import OrgLogo from "./ui/sidebar/OrgLogo";
import Row from "./ui/core/Row";
import { useAuth } from "../contexts/AuthContext";
import { FinishChatAction } from "./ui/header/FinishChatAction";
import { useCapabilities } from "../contexts/permissions/useCapabilities";
import { createPlaceholderClaim } from "../types/claims";
import { useFCCapabilities } from "../contexts/permissions/useFCCapabilities";
import { FilledChatIcon } from "./icons/FilledChatIcon";
import { ContractResearchIcon } from "./icons/ContractResearchIcon";

const Header: React.FC = observer(() => {
  const { is_external } = useAuth();
  const appStore = useAppStore();

  const chatStore = useChatStore();
  const {
    toggleSidebar,
    sidebarWidth,
    setContractToPreview,
    setPolicyToPreview,
  } = useAppStore();
  const {
    clearChatCategoryStore,
    setIsPoliciesDocumentsDrawerOpen,
    setIsAskingReveForPolicy,
    setIsAskingReveForContract,
  } = useChatCategoryStore();
  const { setFilterByClaimId } = useChannels();
  const {
    setClaimToPreview,
    setClaimActivePredefaultQuestion,
    setTempClaimNumber,
  } = useClaimsStore();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showSidebar, showHome, showDashboard, showClaims } =
    useCapabilities();
  const { header } = useFCCapabilities();

  const headerRef = useRef<HTMLElement>(null);
  const pendingReset = useRef(false);

  const isSidebarContentHidden = location.pathname === "/dashboard";
  const isChannelsPage = location.pathname?.includes("/channels/");

  // Show the Finish button only if user is in chat details page and chat is not closed
  const showFinishAction =
    is_external &&
    isChannelsPage &&
    (!chatStore.activeChatId ||
      chatStore.activeChatId === chatStore?.chatDetails?.id) &&
    !chatStore?.chatDetails?.closed_at;

  useEffect(() => {
    const handleResize = () => {
      if (headerRef.current) {
        const width =
          isSidebarContentHidden || !showSidebar
            ? window.innerWidth
            : window.innerWidth - (isMobile ? 0 : sidebarWidth + 6);
        headerRef.current.style.width = `${width}px`;
      }
    };

    // Set initial width
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile, sidebarWidth, isSidebarContentHidden, showSidebar]);

  useEffect(() => {
    // Defer chat store reset until after navigation completes — resetting before
    // the URL changes would cause the current page to briefly render a wrong state.
    if (pendingReset.current) {
      pendingReset.current = false;
      chatStore.reset();
    }
  }, [location.pathname]);

  const clearData = () => {
    pendingReset.current = true;
    clearChatCategoryStore(true);
    setClaimToPreview(null);
    setContractToPreview(null);
    setPolicyToPreview(null);
    setIsPoliciesDocumentsDrawerOpen(false);
    // claimActivePredefaultQuestion is cleared by ChatStore.reset() (called
    // below via pendingReset) — see ChatStore.ts.

    // ChatStore.reset() not called when we are in /claims page
    if (location.pathname?.includes("claims")) {
      setClaimActivePredefaultQuestion(null);
    }
  };

  const redirectCallback = () => {
    clearData();
  };

  // Clicking Policies/Contracts while already on that page (e.g. from the Ask
  // Reve flow) doesn't remount PolicyQuestions/ContractResearch, so their mount
  // effect never refetches — clearData's clearChatCategoryStore(true) would
  // leave the table empty. Just leave the Ask Reve flow and keep the loaded
  // rows/filters.
  const policiesRedirectCallback = () => {
    if (location.pathname.startsWith("/policy-questions")) {
      setPolicyToPreview(null);
      setIsPoliciesDocumentsDrawerOpen(false);
      setIsAskingReveForPolicy(false);
      return;
    }
    clearData();
  };

  const contractsRedirectCallback = () => {
    if (location.pathname.startsWith("/contract-research")) {
      setContractToPreview(null);
      setIsAskingReveForContract(false);
      return;
    }
    clearData();
  };

  // Navigating to Claims from a chat that has a claim attached should land on
  // that claim already selected in the table, the same way the Sidebar's
  // delete-chat redirect does via tempClaimNumber. filterByClaimId and
  // claimToPreview are set too (the same pair ClaimsTable's row click sets)
  // so the sidebar stays filtered to that claim's chats — and has a claim to
  // show/dismiss via ClaimPreview — instead of being cleared by the generic
  // redirectCallback above.
  const claimsRedirectCallback = () => {
    const claimNumber = isChannelsPage ? chatStore.chatClaimNumber : null;
    clearData();

    if (claimNumber && chatStore.claimModeEnabled) {
      setTempClaimNumber(claimNumber);
      setFilterByClaimId(claimNumber);
      setClaimToPreview(createPlaceholderClaim(claimNumber));
    }
  };

  const navItems: NavItem[] = [
    ...(!showHome || header?.home === false
      ? []
      : [
          {
            path: "/",
            label: "Home",
            icon: HomeIcon,
            isActive: (pathname: string) => pathname === "/",
            redirectCallback,
          },
        ]),
    ...(!showClaims || header?.claims === false
      ? []
      : [
          {
            path: "/claims",
            label: "Claims",
            icon: ClaimsIcon,
            isActive: (pathname: string) => pathname.startsWith("/claims"),
            redirectCallback: claimsRedirectCallback,
          },
        ]),
    ...(!header?.policies
      ? []
      : [
          {
            path: "/policy-questions",
            label: "Policies",
            icon: FilledChatIcon,
            isActive: (pathname: string) =>
              pathname.startsWith("/policy-questions"),
            redirectCallback: policiesRedirectCallback,
          },
        ]),
    ...(!header?.contracts
      ? []
      : [
          {
            path: "/contract-research",
            label: "Contracts",
            icon: ContractResearchIcon,
            isActive: (pathname: string) =>
              pathname.startsWith("/contract-research"),
            redirectCallback: contractsRedirectCallback,
          },
        ]),
    ...(!showDashboard || header?.dashboard === false
      ? []
      : [
          {
            path: "/dashboard",
            label: "Dashboard",
            icon: DashboardIcon,
            isActive: (pathname: string) => pathname.startsWith("/dashboard"),
            redirectCallback,
          },
        ]),
  ];

  const dividerSx = {
    height: "100%",
    borderColor: theme.palette.divider,
    margin: "auto",
  };

  return (
    <Box
      component="header"
      ref={headerRef}
      sx={{
        display: "flex",
        height: "var(--height-header, 64px)",
        maxHeight: "var(--height-header, 64px)",
        alignItems: "center",
        px: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,
        overflowX: "auto",
        overflowY: "hidden",
      }}
    >
      {isSidebarContentHidden ? (
        <>
          <Row sx={{ width: "auto", padding: "0px 20px" }}>
            <OrgLogo />
          </Row>
          <Divider orientation="vertical" flexItem sx={dividerSx} />
        </>
      ) : null}

      {/* Mobile sidebar toggle button */}
      {isMobile ? (
        <>
          <IconButton
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
            sx={{
              ml: 1,
              mr: 1,
              width: 40,
              height: 40,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Box
              sx={{
                color: theme.palette.text.primary,
                "& svg": { width: 20, height: 16 },
              }}
            >
              <ThumbnailIcon className="" />
            </Box>
          </IconButton>

          <Divider orientation="vertical" flexItem sx={dividerSx} />
        </>
      ) : null}

      {/* Navigation items */}
      {navItems.map((item) => (
        <NavItemComponent
          key={item.path}
          item={item}
          currentPathname={location.pathname}
          theme={theme}
          isMobile={isMobile}
          iconWrapperSx={item?.iconWrapperSx}
          dividerSx={dividerSx}
          redirectCallback={item.redirectCallback}
        />
      ))}

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Only for external users with a bound chat session, and only while the url matches it */}
      {showFinishAction ? <FinishChatAction /> : null}

      <UserProfileMenu />

      {appStore.isTicketingEnabled ? (
        <>
          <Divider orientation="vertical" flexItem sx={dividerSx} />
          <NavItemComponent
            item={{
              path: "/tickets",
              label: "Tickets",
              icon: HelpIcon,
              isActive: (pathname: string) => pathname.startsWith("/tickets"),
              redirectCallback,
              iconOnly: true,
            }}
            currentPathname={location.pathname}
            theme={theme}
            isMobile={isMobile}
            dividerSx={dividerSx}
            redirectCallback={redirectCallback}
            hideDivider
          />
        </>
      ) : null}
    </Box>
  );
});

export default memo(Header);
